import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import { Theme, Typography, makeStyles } from '@material-ui/core'
import { Timeline } from '@material-ui/lab'
import {
    ImportGuideInfo,
    ImportUploadFileInfo,
    importGuideSelectionsType,
} from 'models/projectImport'
import React from 'react'
import ImportGuideMeter from './components/ImportGuideMeter'
import ImportGuideSelection from './components/ImportGuideSelection'
const useStyle = makeStyles((theme: Theme) => ({
    container: {
        flexGrow: 1,
        display: 'flex',
        flexDirection: 'row',
        backgroundColor: theme.palette.background.paper,
        height: '100%',
    },
    guideWrapper: {
        borderRadius: 0,
        overflow: (props: ImportGuidePageStyleProps) =>
            props.isBPImport && props.isTableEmpty ? 'none' : 'auto',
        minWidth: 500,
        position: 'relative',
        width: '100%',
        minHeight: 700,
        '& .MuiTabs-root': {
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
        },
        padding: (props: ImportGuidePageStyleProps) =>
            props.isBPImport ? theme.spacing(0, 1.25) : theme.spacing(0, 2.5),
        [theme.breakpoints.between('lg', 'md')]: {
            flex: 2,
            margin: theme.spacing(2),
        },
        [theme.breakpoints.down('md')]: {
            flex: 2,
            margin: theme.spacing(2),
        },
        fontSize: pxToRem(14),

        height: (props: ImportGuidePageStyleProps) =>
            props.isBPImport ? 700 : 680,
        alignItems: 'center',
        justifyContent: 'center',
        background: '#FFFFFF',
        boxShadow: (props: ImportGuidePageStyleProps) =>
            props.isBPImport ? 'none' : '0px 4px 4px rgba(0, 0, 0, 0.25);',
        border: (props: ImportGuidePageStyleProps) =>
            props.isBPImport ? '1px solid #DFDFDF' : 'none',
    },
    elementTitle: {
        marginTop: theme.spacing(2),
    },
    elementText: {
        fontSize: pxToRem(24),
        lineHeight: '30px',
    },
    elementType: {
        fontSize: pxToRem(14),
        lineHeight: '30px',
        fontWeight: 500,
        textTransform: 'capitalize',
    },
    elementHeader: {
        fontSize: pxToRem(18),
    },
    elementMeters: {
        marginTop: theme.spacing(0.5),
    },
    elementMeterGrid: {
        display: 'flex',
    },
    elementMeterContainer: {
        textAlign: 'center',
    },
    elementQuestions: {
        marginTop: theme.spacing(2),
    },
    elementSelections: {
        padding: 0,
        marginBottom: 0,
    },
    timelineItem: {
        '&:before': {
            flex: 0,
            padding: 0,
        },
    },
    timelineAvatar: {
        width: theme.spacing(2),
        height: theme.spacing(2),
    },
    firstContent: {
        paddingTop: theme.spacing(0.25),
    },
    middleContent: {
        display: 'flex',
        alignItems: 'center',
    },
    lastContent: {
        display: 'flex',
        alignItems: 'end',
        paddingBottom: theme.spacing(0.25),
    },
    statusIcons: {
        marginTop: 0,
        marginBottom: 0,
    },
    noDataContainer: {
        overflow: 'hidden',
    },
}))
interface ImportGuidePageStyleProps {
    isBPImport?: boolean
    isTableEmpty?: boolean
}
export interface ImportGuidePageProps extends ImportGuidePageStyleProps {
    selectedFile?: Partial<ImportUploadFileInfo> | undefined
    selectedTypeName?: string
    selectedRowType?: string
    isSelectedFileExisting?: boolean
    handleGuideSelectionClick?: (
        fileKey: string,
        label: string,
        value: boolean,
        points: number
    ) => void
}
const ImportGuidePage: React.FC<ImportGuidePageProps> = ({
    selectedFile,
    selectedTypeName,
    selectedRowType,
    isTableEmpty,
    isBPImport,
    isSelectedFileExisting,
    handleGuideSelectionClick,
}) => {
    const classes = useStyle({
        isBPImport: isBPImport,
        isTableEmpty: isTableEmpty,
    })
    let container = document.getElementById('root')
    React.useEffect(() => {
        if (container && container.children) {
            let requiredChild = container.children[0].children[1]
            requiredChild.classList.add(classes.noDataContainer)
        }
    }, [container])
    const importGuideSelections: importGuideSelectionsType[] = [
        {
            label: 'needAttentions',
            type: 'prerequisite',
            text: 'Have all "Needs Attention" errors been resolved?',
            points: 25,
        },
        {
            label: 'anyOpenCautions',
            type: 'recommendation',
            text: 'Have all "Caution" errors been resolved?',
            points: 15,
        },
        {
            label: 'haveSetAssemblyAsso',
            type: 'recommendation',
            text: 'Have you set assembly associations?',
            points: 20,
        },
        {
            label: 'haveDownldMasterSpecTemplate',
            type: 'recommendation',
            text:
                'Has the MasterSpec template file been applied to the imported file?',
            points: 10,
        },
        {
            label: 'haveProductTypeAppeared',
            type: 'recommendation',
            text:
                'Do all of the Family Product Types appear on the import page column?',
            points: 20,
        },
        {
            label: 'haveManufacturerAppeared',
            type: 'recommendation',
            text:
                'Do the Manufacturers appear as pills within the Family Product Type?',
            points: 10,
        },
    ]
    const prerequisites = importGuideSelections.filter(
        (importGuideSelection) => importGuideSelection.type === 'prerequisite'
    )
    const recommendations = importGuideSelections.filter(
        (importGuideSelection) => importGuideSelection.type === 'recommendation'
    )
    return (
        <div className={classes.guideWrapper}>
            <GridItem
                className={classes.elementTitle}
                data-testid="elementTitle"
            >
                <Typography className={classes.elementText}>
                    {selectedTypeName}
                </Typography>
                <Typography className={classes.elementType}>
                    {selectedRowType === 'ProductType'
                        ? 'Product Type'
                        : selectedRowType}
                </Typography>
            </GridItem>
            <GridItem
                className={classes.elementMeters}
                data-testid="elementMeters"
            >
                <GridContainer>
                    <GridItem className={classes.elementMeterGrid} xs={6}>
                        <div className={classes.elementMeterContainer}>
                            <Typography className={classes.elementHeader}>
                                Steps Completed
                            </Typography>
                            <ImportGuideMeter
                                data-testid="stepsCompleted"
                                variant="determinate"
                                value={
                                    ((selectedFile?.importGuideInfo
                                        ?.totalSteps as number) /
                                        6) *
                                    100
                                }
                                label={`${selectedFile?.importGuideInfo?.totalSteps}/6`}
                                size={100}
                                thickness={5}
                            />
                        </div>
                    </GridItem>
                    <GridItem className={classes.elementMeterGrid} xs={6}>
                        <div className={classes.elementMeterContainer}>
                            <Typography className={classes.elementHeader}>
                                Import Score
                            </Typography>
                            <ImportGuideMeter
                                data-testid="importScore"
                                variant="determinate"
                                value={
                                    selectedFile?.importGuideInfo?.valueScore
                                }
                                label={String(
                                    selectedFile?.importGuideInfo?.valueScore
                                )}
                                size={100}
                                thickness={5}
                            />
                        </div>
                    </GridItem>
                </GridContainer>
            </GridItem>
            <GridItem className={classes.elementQuestions}>
                <GridItem>
                    <Typography className={classes.elementHeader}>
                        Prerequisites
                    </Typography>
                    <Timeline className={classes.elementSelections}>
                        {prerequisites.map((prerequisite, index) => {
                            let position: string
                            position = 'none'
                            return (
                                <ImportGuideSelection
                                    key={`${prerequisite.label}_${index}`}
                                    fileKey={selectedFile?.fileKey}
                                    label={prerequisite.label}
                                    type={prerequisite.type}
                                    text={prerequisite.text}
                                    points={prerequisite.points}
                                    value={
                                        selectedFile?.importGuideInfo?.[
                                            prerequisite.label as keyof ImportGuideInfo
                                        ]
                                    }
                                    position={position}
                                    isSelectedFileExisting={
                                        isSelectedFileExisting
                                    }
                                    handleGuideSelectionClick={
                                        handleGuideSelectionClick
                                    }
                                />
                            )
                        })}
                    </Timeline>
                </GridItem>
                <GridItem>
                    <Typography className={classes.elementHeader}>
                        Recommendations
                    </Typography>

                    <Timeline className={classes.elementSelections}>
                        {recommendations
                            .filter(
                                (importGuideSelection) =>
                                    importGuideSelection.type ===
                                    'recommendation'
                            )
                            .map((prerequisite, index) => {
                                let position: string
                                if (index === 0) {
                                    position = 'first'
                                } else if (
                                    index ===
                                    recommendations.length - 1
                                ) {
                                    position = 'last'
                                } else {
                                    position = 'middle'
                                }
                                return (
                                    <ImportGuideSelection
                                        fileKey={selectedFile?.fileKey}
                                        label={prerequisite.label}
                                        type={prerequisite.type}
                                        text={prerequisite.text}
                                        points={prerequisite.points}
                                        value={
                                            selectedFile?.importGuideInfo?.[
                                                prerequisite.label as keyof ImportGuideInfo
                                            ]
                                        }
                                        position={position}
                                        isSelectedFileExisting={
                                            isSelectedFileExisting
                                        }
                                        handleGuideSelectionClick={
                                            handleGuideSelectionClick
                                        }
                                    />
                                )
                            })}
                    </Timeline>
                </GridItem>
            </GridItem>
        </div>
    )
}

export default ImportGuidePage
